import React, { ReactNode, useState } from "react";
import "./Field.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";

interface FieldProps {
  icon?: ReactNode;
  defaultValue?: string;
  id?: string;
  class?: string;
  clear?: boolean;
  onInput?: (event: React.FormEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FormEvent<HTMLInputElement>) => void;
  size?: "fill" | "default";
}

interface TextFieldProps extends FieldProps {
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
}

interface NumberFieldProps extends FieldProps {
  value: number;
  min?: number;
  max?: number;
  range?: number;
}

export function TextField(props: TextFieldProps) {
  const [openClearButton, setOpenClearButton] = useState(false);

  function showClearButton(event: React.FormEvent<HTMLInputElement>) {
    const input = event.currentTarget as HTMLInputElement;
    if (!input.value || !props.clear) {
      if (props.clear) setOpenClearButton(false);
      return;
    }

    setOpenClearButton(true);
  }

  function clearInput(event: React.MouseEvent<SVGSVGElement, MouseEvent>) {
    const input = event.currentTarget.parentElement?.getElementsByTagName(
      "input",
    )[0] as HTMLInputElement;
    input.value = "";
    setOpenClearButton(false);
    if (props.onInput) {
      const customEvent = {
        target: input,
        currentTarget: input,
        bubbles: true,
        cancelable: true,
        defaultPrevented: false,
        eventPhase: 0,
        isTrusted: true,
        nativeEvent: new Event("input"),
        preventDefault: () => {},
        isDefaultPrevented: () => false,
        stopPropagation: () => {},
        isPropagationStopped: () => false,
        persist: () => {},
        timeStamp: Date.now(),
        type: "input",
      } as React.FormEvent<HTMLInputElement>;
      props.onInput(customEvent);
    }
    input.focus();
  }

  function isFocus(event: React.FocusEvent<HTMLInputElement, Element>) {
    const input = event.currentTarget.parentElement as HTMLInputElement;
    input.style.boxShadow = "0 0 2px 2px var(--brand-3)";
  }

  function isBlur(event: React.FocusEvent<HTMLInputElement, Element>) {
    const input = event.target.parentElement as HTMLInputElement;
    input.removeAttribute("style");
  }

  return (
    <div className={props.size === "fill" ? "inputFillContainer" : "inputContainer"}>
      {props.icon && <div className="iconContainer">{props.icon}</div>}
      <input
        type="text"
        placeholder={props.placeholder}
        defaultValue={props.defaultValue}
        id={props.id}
        className={props.class}
        onInput={(event) => {
          showClearButton(event);
          if (props.onInput) props.onInput(event);
        }}
        minLength={props.minLength ?? 0}
        maxLength={props.maxLength ?? 225}
        onFocus={isFocus}
        onBlur={isBlur}
        style={
          props.size === "fill"
            ? {
                width: "100%",
              }
            : {}
        }
      />
      {props.clear && openClearButton && (
        <FontAwesomeIcon className="iconClear" onClick={clearInput} icon={faClose} />
      )}
    </div>
  );
}

export function NumberField(props: NumberFieldProps) {
  const [openClearButton, setOpenClearButton] = useState(false);
  // Controlled value: mirror `props.value` into local state so the input
  // reflects async/late updates (e.g. budget limits resolved by getBudgetLimit
  // or switching the selected Hall component). An uncontrolled `defaultValue`
  // would freeze the field at its first render value.
  const [value, setValue] = useState<string>(String(props.value));

  React.useEffect(() => {
    setValue(String(props.value));
  }, [props.value]);

  function showClearButton(event: React.FormEvent<HTMLInputElement>) {
    const input = event.currentTarget as HTMLInputElement;
    if (!input.value || !props.clear) {
      if (props.clear) setOpenClearButton(false);
      return;
    }

    setOpenClearButton(true);
  }

  function clearInput(event: React.MouseEvent<SVGSVGElement, MouseEvent>) {
    const input = event.currentTarget.parentElement?.getElementsByTagName(
      "input",
    )[0] as HTMLInputElement;
    input.value = "";
    setValue("");
    setOpenClearButton(false);
    if (props.onInput) {
      const customEvent = {
        target: input,
        currentTarget: input,
        bubbles: true,
        cancelable: true,
        defaultPrevented: false,
        eventPhase: 0,
        isTrusted: true,
        nativeEvent: new Event("input"),
        preventDefault: () => {},
        isDefaultPrevented: () => false,
        stopPropagation: () => {},
        isPropagationStopped: () => false,
        persist: () => {},
        timeStamp: Date.now(),
        type: "input",
      } as React.FormEvent<HTMLInputElement>;
      props.onInput(customEvent);
    }
    input.focus();
  }

  function isFocus(event: React.FocusEvent<HTMLInputElement, Element>) {
    const input = event.currentTarget.parentElement as HTMLInputElement;
    input.style.boxShadow = "0 0 2px 2px var(--brand-3)";
  }

  function isBlur(event: React.FocusEvent<HTMLInputElement, Element>) {
    const input = event.target.parentElement as HTMLInputElement;
    input.removeAttribute("style");
    if (props.onBlur) props.onBlur(event);
  }

  function onlyNumbers(event: React.FormEvent<HTMLInputElement>) {
    event.currentTarget.value = event.currentTarget.value.replace(/[^0-9-.]/g, "");
    if (isNaN(Number(event.currentTarget.value))) {
      event.currentTarget.value = String(props.min || 0);
    }

    if (props.min !== undefined && props.min > Number(event.currentTarget.value)) {
      event.currentTarget.value = String(props.min);
    } else if (props.max !== undefined && props.max < Number(event.currentTarget.value)) {
      event.currentTarget.value = String(props.max);
    }
    // Keep controlled state in sync with the sanitized DOM value.
    setValue(event.currentTarget.value);
  }

  function addRangeToValue(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "ArrowUp") return;
    if (props.max !== undefined && props.max === Number(event.currentTarget.value)) return;
    const range = props.range ?? 1;
    event.currentTarget.value = String(Number(event.currentTarget.value) + range);
    setValue(event.currentTarget.value);
    if (props.onInput) props.onInput(event);
  }

  function subRangeToValue(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "ArrowDown") return;
    if (props.min !== undefined && props.min >= Number(event.currentTarget.value)) return;
    const range = props.range ?? 1;
    event.currentTarget.value = String(Number(event.currentTarget.value) - range);
    setValue(event.currentTarget.value);
    if (props.onInput) props.onInput(event);
  }

  return (
    <div className={props.size === "fill" ? "inputFillContainer" : "inputContainer"}>
      {props.icon && <div className="iconContainer">{props.icon}</div>}
      <input
        type="text"
        value={value}
        id={props.id}
        className={props.class}
        onKeyDown={(event) => {
          addRangeToValue(event);
          subRangeToValue(event);
        }}
        onChange={(event) => {
          onlyNumbers(event);
          showClearButton(event);
          if (props.onInput) props.onInput(event);
        }}
        onFocus={isFocus}
        onBlur={isBlur}
        style={
          props.size === "fill"
            ? {
                width: "100%",
              }
            : {}
        }
      />
      {props.clear && openClearButton && (
        <FontAwesomeIcon className="iconClear" onClick={clearInput} icon={faClose} />
      )}
    </div>
  );
}
